import { useId } from "react";
import { Field } from "react-final-form";
import type { BaseFieldProps, FieldOption, FieldValue, SimpleFieldValue } from "../../types/form";
import { composeValidators } from "../../utils/validation";
import { ReactSelect, type SelectProps } from "./components/select/react-select";
import { FormHelperText } from "./form-helper-text";

type Props<T extends FieldValue> = BaseFieldProps<T> &
	Omit<SelectProps, "onChange" | "currentValue" | "selectType">;

export const ReactSelectField = <T extends FieldValue = SimpleFieldValue>({
	options,
	name,
	label,
	ariaLabel,
	validators,
	initialValue,
	helperText,
	labeledBy,
	floatingLabel,
	testId,
	placeholder,
	isOptionDisabled,
	menuPortalTarget,
	isDisabled,
	...otherProps
}: Props<T>) => {
	const describedById = useId();

	return (
		<Field<T>
			name={name}
			initialValue={initialValue}
			validate={validators && composeValidators(...validators)}
			{...otherProps}
		>
			{({ input, meta }) => {
				const hasError = !!meta.error && !!meta.touched;
				const handleChange = (option: unknown) => {
					const fiedOption = option as FieldOption;
					input.onChange(fiedOption.value);
				};

				const value = input.value !== null ? options.find((i) => i.value === input.value) : null;

				return (
					<>
						<ReactSelect
							options={options}
							onChange={handleChange}
							label={label}
							hideSelectedOptions={false}
							defaultValue={value}
							value={value}
							labeledBy={labeledBy}
							testId={testId}
							floatingLabel={floatingLabel}
							aria-label={ariaLabel}
							placeholder={placeholder}
							menuPortalTarget={menuPortalTarget}
							error={hasError}
							isOptionDisabled={isOptionDisabled}
							isDisabled={isDisabled}
						/>

						<FormHelperText error={hasError} id={describedById}>
							{hasError ? meta.error : helperText}
						</FormHelperText>
					</>
				);
			}}
		</Field>
	);
};
